// ABOUTME: Manages subscription status, feature gating, and upgrade flows
// ABOUTME: Provides freemium limits and premium feature checks

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type {
  SubscriptionLimits,
  SubscriptionPlan,
  SubscriptionStatus,
  SubscriptionTier,
} from '@/types/models'

/**
 * Free tier limits
 */
const FREE_LIMITS: SubscriptionLimits = {
  maxWardrobeItems: 20,
  maxOutfits: 5,
  maxLevel: 3,
  maxActiveChallenges: 3,
  colorProfileAccess: false,
  advancedRecommendations: false,
  adFree: false,
  prioritySupport: false,
}

/**
 * Premium tier limits
 */
const PREMIUM_LIMITS: SubscriptionLimits = {
  maxWardrobeItems: Infinity,
  maxOutfits: Infinity,
  maxLevel: 10,
  maxActiveChallenges: Infinity,
  colorProfileAccess: true,
  advancedRecommendations: true,
  adFree: true,
  prioritySupport: true,
}

/**
 * Available subscription plans
 */
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    tier: 'free',
    name: 'Free',
    description: 'Get started with basic features',
    price: 0,
    currency: 'USD',
    period: 'monthly',
    features: [
      'Up to 20 wardrobe items',
      'Up to 5 outfit combinations',
      'Access to Levels 1-3',
      'Basic style recommendations',
      '3 active challenges',
    ],
    limits: FREE_LIMITS,
  },
  {
    id: 'premium-monthly',
    tier: 'premium',
    name: 'Premium Monthly',
    description: 'Full access, billed monthly',
    price: 9.99,
    currency: 'USD',
    period: 'monthly',
    features: [
      'Unlimited wardrobe items',
      'Unlimited outfit combinations',
      'Access to all 10 levels',
      'Advanced AI recommendations',
      'Unlimited challenges',
      'Color profile analysis',
      'Ad-free experience',
      'Priority support',
    ],
    limits: PREMIUM_LIMITS,
  },
  {
    id: 'premium-yearly',
    tier: 'premium',
    name: 'Premium Yearly',
    description: 'Best value - 2 months free!',
    price: 79.99,
    currency: 'USD',
    period: 'yearly',
    features: [
      'Unlimited wardrobe items',
      'Unlimited outfit combinations',
      'Access to all 10 levels',
      'Advanced AI recommendations',
      'Unlimited challenges',
      'Color profile analysis',
      'Ad-free experience',
      'Priority support',
    ],
    limits: PREMIUM_LIMITS,
    popular: true,
  },
]

/**
 * Get limits for a tier
 */
export function getLimitsForTier(tier: SubscriptionTier): SubscriptionLimits {
  return tier === 'premium' ? PREMIUM_LIMITS : FREE_LIMITS
}

/**
 * Subscription store state
 */
interface SubscriptionState {
  subscription: SubscriptionStatus
  isLoading: boolean
}

/**
 * Subscription store actions
 */
interface SubscriptionActions {
  setSubscription: (subscription: SubscriptionStatus) => void
  setLoading: (loading: boolean) => void
  canAddWardrobeItem: (currentCount: number) => boolean
  canCreateOutfit: (currentCount: number) => boolean
  canAccessLevel: (level: number) => boolean
  canAddChallenge: (currentCount: number) => boolean
  hasColorProfileAccess: () => boolean
  hasAdvancedRecommendations: () => boolean
  isAdFree: () => boolean
  startTrial: () => Promise<void>
  subscribe: (planId: string) => Promise<void>
  cancelSubscription: () => Promise<void>
  getLimits: () => SubscriptionLimits
  getUsagePercentage: (
    feature: 'wardrobeItems' | 'outfits' | 'challenges',
    current: number
  ) => number
  reset: () => void
}

type SubscriptionContextValue = SubscriptionState & SubscriptionActions

const STORAGE_KEY = 'gentleman-subscription-store'

const initialSubscription: SubscriptionStatus = {
  tier: 'free',
  status: 'none',
}

function loadPersistedState(): SubscriptionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        subscription: parsed.subscription ?? initialSubscription,
        isLoading: false,
      }
    }
  } catch {}
  return { subscription: initialSubscription, isLoading: false }
}

function persistState(state: SubscriptionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ subscription: state.subscription }))
  } catch {}
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

export function SubscriptionStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SubscriptionState>(loadPersistedState)

  const update = useCallback((updater: (prev: SubscriptionState) => SubscriptionState) => {
    setState((prev) => {
      const next = updater(prev)
      persistState(next)
      return next
    })
  }, [])

  const setSubscription = useCallback(
    (subscription: SubscriptionStatus) => {
      update((s) => ({ ...s, subscription }))
    },
    [update]
  )

  const setLoading = useCallback((isLoading: boolean) => {
    setState((s) => ({ ...s, isLoading }))
  }, [])

  const getLimits = useCallback(
    () => getLimitsForTier(state.subscription.tier),
    [state.subscription.tier]
  )

  const canAddWardrobeItem = useCallback(
    (currentCount: number) =>
      currentCount < getLimitsForTier(state.subscription.tier).maxWardrobeItems,
    [state.subscription.tier]
  )

  const canCreateOutfit = useCallback(
    (currentCount: number) => currentCount < getLimitsForTier(state.subscription.tier).maxOutfits,
    [state.subscription.tier]
  )

  const canAccessLevel = useCallback(
    (level: number) => level <= getLimitsForTier(state.subscription.tier).maxLevel,
    [state.subscription.tier]
  )

  const canAddChallenge = useCallback(
    (currentCount: number) =>
      currentCount < getLimitsForTier(state.subscription.tier).maxActiveChallenges,
    [state.subscription.tier]
  )

  const hasColorProfileAccess = useCallback(
    () => getLimitsForTier(state.subscription.tier).colorProfileAccess,
    [state.subscription.tier]
  )

  const hasAdvancedRecommendations = useCallback(
    () => getLimitsForTier(state.subscription.tier).advancedRecommendations,
    [state.subscription.tier]
  )

  const isAdFree = useCallback(
    () => getLimitsForTier(state.subscription.tier).adFree,
    [state.subscription.tier]
  )

  const startTrial = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true }))
    try {
      const trialEnd = new Date()
      trialEnd.setDate(trialEnd.getDate() + 7)
      update((s) => ({
        ...s,
        subscription: {
          tier: 'premium',
          status: 'trialing',
          trialEnd,
          currentPeriodStart: new Date(),
          currentPeriodEnd: trialEnd,
        },
      }))
    } finally {
      setState((s) => ({ ...s, isLoading: false }))
    }
  }, [update])

  const subscribe = useCallback(
    async (planId: string) => {
      setState((s) => ({ ...s, isLoading: true }))
      try {
        const plan = SUBSCRIPTION_PLANS.find((p) => p.id === planId)
        if (!plan) throw new Error('Plan not found')

        const now = new Date()
        const periodEnd = new Date()
        if (plan.period === 'yearly') {
          periodEnd.setFullYear(periodEnd.getFullYear() + 1)
        } else {
          periodEnd.setMonth(periodEnd.getMonth() + 1)
        }

        update((s) => ({
          ...s,
          subscription: {
            tier: plan.tier,
            plan,
            status: 'active',
            currentPeriodStart: now,
            currentPeriodEnd: periodEnd,
          },
        }))


      } finally {
        setState((s) => ({ ...s, isLoading: false }))
      }
    },
    [update]
  )

  const cancelSubscription = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true }))
    try {
      update((s) => ({
        ...s,
        subscription: {
          ...s.subscription,
          status: 'canceled',
          cancelAtPeriodEnd: true,
        },
      }))
    } finally {
      setState((s) => ({ ...s, isLoading: false }))
    }
  }, [update])

  const getUsagePercentage = useCallback(
    (feature: 'wardrobeItems' | 'outfits' | 'challenges', current: number) => {
      const limits = getLimitsForTier(state.subscription.tier)
      let max: number

      switch (feature) {
        case 'wardrobeItems':
          max = limits.maxWardrobeItems
          break
        case 'outfits':
          max = limits.maxOutfits
          break
        case 'challenges':
          max = limits.maxActiveChallenges
          break
        default:
          return 0
      }

      if (max === Infinity) return 0
      return Math.min((current / max) * 100, 100)
    },
    [state.subscription.tier]
  )

  const reset = useCallback(() => {
    const initial = { subscription: initialSubscription, isLoading: false }
    setState(initial)
    persistState(initial)
  }, [])

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      ...state,
      setSubscription,
      setLoading,
      canAddWardrobeItem,
      canCreateOutfit,
      canAccessLevel,
      canAddChallenge,
      hasColorProfileAccess,
      hasAdvancedRecommendations,
      isAdFree,
      startTrial,
      subscribe,
      cancelSubscription,
      getLimits,
      getUsagePercentage,
      reset,
    }),
    [
      state,
      setSubscription,
      setLoading,
      canAddWardrobeItem,
      canCreateOutfit,
      canAccessLevel,
      canAddChallenge,
      hasColorProfileAccess,
      hasAdvancedRecommendations,
      isAdFree,
      startTrial,
      subscribe,
      cancelSubscription,
      getLimits,
      getUsagePercentage,
      reset,
    ]
  )

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
}

export function useSubscriptionStore(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscriptionStore must be used within SubscriptionStoreProvider')
  return ctx
}

/**
 * Hook to check if user is premium
 */
export function useIsPremium(): boolean {
  const { subscription } = useSubscriptionStore()
  return (
    subscription.tier === 'premium' &&
    (subscription.status === 'active' || subscription.status === 'trialing')
  )
}

/**
 * Hook to get subscription limits
 */
export function useSubscriptionLimits(): SubscriptionLimits {
  const { getLimits } = useSubscriptionStore()
  return getLimits()
}
